import React, {useState, useEffect} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import {toTime, toTimeType, timeConverter, isObjectEmpty, uploadCSV, replaceAll, timeConverterSM} from '../../../Utils';
import Spinner from '../../common/generic/Spinner';

const ContainerComp = ({pageState, reportState, csvData, setcsvData}) =>{
    const {database, deleteThis, request, fetchModules, modulesData, modulesReady } = pageState;
    const [processedMarks, setProcessedMarks] = useState({});
    const [markRecords, setMarkRecords] = useState({});
    const {selectedStudents, selectedProgram} = reportState;

    /*
    CSV NOTES
    =========
    There is a big problem when trying to upload more then 1 csv file.  The below system will reset all the data in 
    this component and start it over when when the user clicks save.
    
    Steps
    1) User clicks save in either ProgramReportTableComp or AcitivityReportComp
        1b) openCSVPage() will populate csvData using setcsvData({processStage:'newRequest'});
    2) ViewReportsPage checks to see if the marks have changed (* seems to be called way to many times...)
        2b) setReportType(csvData.reportType);
            setSelectedProgram({...csvData.selectedProgram});
            setSelectedStudents([...csvData.selectedStudents]); 
    3) Start the report
        3a) CSV Step 1: checks if a new request for csv and starts to clear the data
        3b) CSV Step 2: clears the data 
        3c) CSV Step 3: keep checking to see when the data has been cleared
        3d) CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
        3e) (The normal Startup starts at CSV Step 1) 
    */

    // CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
    // The normal Startup starts at CSV Step 1 
    const tryLoadingCSVStage2 = () => {
        if(!csvData) return;
        if(csvData.processStage === 'clearingData'){
            if(!database['getProgramMarks'] && isObjectEmpty(markRecords) && isObjectEmpty(processedMarks)){
                setcsvData({...csvData, processStage:'clearedData'});
            }
        }
    };
    // CSV Step 3: keep checking to see when the data has been cleared
    useEffect(() =>{tryLoadingCSVStage2();},[database['getProgramMarks']]);
    useEffect(() =>{tryLoadingCSVStage2();},[markRecords]);
    useEffect(() =>{tryLoadingCSVStage2();},[processedMarks]);

    // CSV Step 2: clears the data
    useEffect(() => {
        if(!csvData) return;

        if(csvData.processStage === 'clearingData'){
            deleteThis('getProgramMarks');
            setMarkRecords({});
            setProcessedMarks({});
        }
    },[csvData]);

    // CSV Step 1: checks if a new request for csv and starts to clear the data 
    // Normal Startup Step 1:
    useEffect(() => {
        if(csvData){
            if(csvData.processStage==='newRequest'){
                // csvData.setloadingCSV({...csvData.loadingCSV, activity:true});
                setcsvData({...csvData, processStage:'clearingData'});
                return;
            }

            if(csvData.processStage==='clearingData') return;
        }

        if(!isObjectEmpty(markRecords)) return; // already loading

        if(!database['getProgramMarks']){
            let studentQueries = selectedStudents.map(studentRec=>{
                let getMarksQuery={
                    params: studentRec.id+'~'+selectedProgram.id.toLowerCase(),
                    func:'getProgramMarks'            
                };
                return getMarksQuery;
            });

            if(studentQueries.length > 0){
                let counter = 1;
                studentQueries = studentQueries.map((query,i)=>{
                    let prevQuery = (i!=0) ? studentQueries[i-1] : 'undefinded';
                    if(prevQuery !== 'undefinded'){
                        counter++;
                        query.nextRequest = prevQuery;
                    }
                    return query;
                });
                let loadChainedMarks = studentQueries[studentQueries.length-1];
                loadChainedMarks.numQueries = counter;
                setMarkRecords({info:{numStudents:counter,numLoaded:counter}});

                // pass the link list of saved changes into the database
                request(loadChainedMarks); 
            }
            else
                console.log('Nothing to load?');
        }
    });

    // convert database['getProgramMarks'] into setMarkRecords
    // UDATES - when database['getProgramMarks'] changes
    useEffect(() => {
        if(csvData && csvData.processStage !== 'clearedData') return; // don't setup markRecords for csv files without being ready 
        if(isObjectEmpty(markRecords)) return;
        (!pageState.isFetchingModules && !modulesReady) && fetchModules(selectedProgram.id.toLowerCase());
        if(!database['getProgramMarks']) return;
        
        let marks = {};
        let key = 'unavailable';
        let counter = {...markRecords.info};

        if(database['getProgramMarks'].isLoaded) counter.numLoaded--;
        
        marks = {
            isloaded: database['getProgramMarks'].isLoaded,
            data: database['getProgramMarks'].data
        }; 

        if((marks.data) && !isObjectEmpty(marks.data)){
            key = Object.keys(marks.data)[0].split('+')[0];
        }

        setMarkRecords({...markRecords, info:{...counter}, [key]:marks});

    }, [database['getProgramMarks']]); 

    const tryLoadingNow = () => {
        if(isObjectEmpty(markRecords)) return;
        if(!isObjectEmpty(processedMarks)) return;
        if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return;

        let reportData = JSON.parse(localStorage.getItem('reportData'));
        let masterLevel = parseInt(reportData.masterLevel)/100;

        if(modulesReady) { 
            let studentsModuleMarks = selectedStudents.reduce((acc,studentRec)=>{
                let studentID = studentRec.id;
                let studentMarkRecords = markRecords[studentID];
                if(!studentMarkRecords) return acc; // still loading marks

                let marksPerModule = modulesData.map(moduleRec => { // foreach module
                    let moduleID = moduleRec.moduleID;
                    return moduleRec.links.map(linkRec => // foreach activity in a module attach marks to it.
                    {                   
                        let linkID= linkRec.linkID;
                        if(studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`]) {
                            return {moduleID, linkID, name: linkRec.name, unitName: linkRec.unit.name, mark: studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`]};
                        }
                    }).filter(e=> e); // remove empty records (ones with no marks)
                });

                acc[`${studentRec.id}+${studentRec.name}`] = marksPerModule;
                return acc;
            },{});
            setProcessedMarks(studentsModuleMarks);

            if(csvData){
                let newLine = '%0A';
                let csvString = '';
                csvString =`"* Each teacher can set their own mastery level for the class and an activity is considered to be mastered when the student answers at least that ${masterLevel*100}% of the questions correctly on any attempt"${newLine}`;
                csvString +=`"Student without marks will not appear in this report"${newLine}`;
                let tableHeaderStr = '"Unit Name","Activity Name","Attempts","Mastered","Highest","Avg","Last","Gain",Time","Date"'+newLine;

                Object.keys(studentsModuleMarks).forEach((key) => { 
                    let headerStr = `${newLine}"${key.split('+')[1]}"${newLine}"${selectedProgram.name}"${newLine}"Activity Report"${newLine}`;
                    let tableData  = studentsModuleMarks[key];

                    csvString += headerStr + tableHeaderStr;
                    tableData.forEach((moduleRec) => {
                        let moduleTitleStr = (moduleRec.length > 0) ? `"${moduleRec[0].moduleID[1]}"${newLine}` : '';
                        csvString += moduleTitleStr;
                        moduleRec.forEach((markRec, mark_i) => {
                            const { unitName, name, mark} = markRec;
                            const { TimesAttempted, TimesCompleted, HighestMark, LastMark, FirstMark, QuestionsRight, QuestionsAttempted, CumulativeTime, LastUsed} = mark;

                            let improvement = '--';
                            if(FirstMark && LastMark)
                                improvement = LastMark - FirstMark;
                            if(improvement !== '--')
                                improvement = improvement+'%';
                            if(improvement === '0%')
                                improvement = '--';

                            let markDate = '--';
                            if(LastUsed)
                                markDate = timeConverter(parseInt(LastUsed));

                            let recordStr = `"${(unitName?unitName:'')}","${name}","${TimesAttempted}","${TimesCompleted}","${HighestMark}%","${LastMark}%","${Math.round((QuestionsRight/QuestionsAttempted)*100)}%","${improvement}","${toTime(CumulativeTime)} ${toTimeType(CumulativeTime)}","${markDate}"${newLine}`;
                            csvString += recordStr;
                        });
                    });
                });
                
                let name = '';
                if(Object.keys(studentsModuleMarks).length===0){
                    window.alert('There are no marks for the student(s) selected');
                    csvData.setloadingCSV({...csvData.loadingCSV, activity:false});
                    return;
                }

                if(Object.keys(studentsModuleMarks).length===1)
                    name = Object.keys(studentsModuleMarks)[0].split('+')[1]+'_';

                let today = new Date();
                today = timeConverterSM(today.getTime());

                uploadCSV(name+'ActivityReport_'+selectedProgram.name+'_'+today,csvString);
                csvData.setloadingCSV({...csvData.loadingCSV, activity:false});
            }
        };
    }; 
    useEffect(() =>{tryLoadingNow();},[modulesData]);
    useEffect(() =>{tryLoadingNow();},[markRecords]);

    if(csvData) return <></>;
    if(isObjectEmpty(markRecords)) return <> <Spinner/> <center>Loading... ({database['queryCount']}) </center> </>;
    if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return <> <Spinner/> <center>Loading... ({database['queryCount']}) </center> </>;

    return <>
        {Object.keys(processedMarks).length < selectedStudents.length && <center> <p>  Students without marks will not appear in this report </p></center>}
        {Object.keys(processedMarks).map((key, i) => (
            <span key={i}><br/> 
            <ActivityReportComp header={{student: key.split('+')[1], program: selectedProgram.name}} tableData={processedMarks[key]}/>            
            <br/></span>
        ))}
    </>;
};

const ActivityReportComp = ({header, tableData}) => {
    const [masterLevel, setMasterLevel] = useState(0.8);
    const [totals, setTotals] = useState({
        name:'',
        unitName:'',
        haveTotals:false,
        mark:{
            TimesAttempted:0,
            TimesCompleted:0, 
            HighestMark:0,
            LastMark:0,
            FirstMark:0,
            QuestionsRight:0,
            QuestionsAttempted:0,
            CumulativeTime:0,
            LastUsed:0
        }
    });

    useEffect(()=>{
        let reportData = JSON.parse(localStorage.getItem('reportData'));
        if(masterLevel !== (parseInt(reportData.masterLevel)/100))
            setMasterLevel(parseInt(reportData.masterLevel)/100);

        if(totals.haveTotals)
            return;
        let tmpTotals = {
            name:'',
            unitName:'',
            haveTotals:false,
            numActs:0,
            mark:{
                TimesAttempted:0,
                TimesCompleted:0, 
                HighestMark:0,
                LastMark:0,
                FirstMark:0,
                QuestionsRight:0,
                QuestionsAttempted:0,
                CumulativeTime:0,
                LastUsed:0
            }
        };

        tableData.map((moduleRec) => {            
            moduleRec.map((markRec) => {
                tmpTotals.numActs++;
                tmpTotals.mark.LastUsed = (Number(markRec.mark.LastUsed) > Number(tmpTotals.mark.LastUsed))?markRec.mark.LastUsed:tmpTotals.mark.LastUsed;
                tmpTotals.mark.HighestMark = (Number(markRec.mark.HighestMark) > Number(tmpTotals.mark.HighestMark))?markRec.mark.HighestMark:tmpTotals.mark.HighestMark;
                tmpTotals.mark.LastMark+=Number(markRec.mark.LastMark); 
                tmpTotals.mark.FirstMark+=Number(markRec.mark.FirstMark); 
                tmpTotals.mark.TimesAttempted+=Number(markRec.mark.TimesAttempted); 
                tmpTotals.mark.TimesCompleted+=Number(markRec.mark.TimesCompleted); 
                tmpTotals.mark.QuestionsRight+=Number(markRec.mark.QuestionsRight);
                tmpTotals.mark.QuestionsAttempted+=Number(markRec.mark.QuestionsAttempted);
                tmpTotals.mark.CumulativeTime+=Number(markRec.mark.CumulativeTime);
            });
        });
        tmpTotals.mark.LastMark = Math.round(tmpTotals.mark.LastMark/tmpTotals.numActs);
        tmpTotals.mark.FirstMark = Math.round(tmpTotals.mark.FirstMark/tmpTotals.numActs);
        tmpTotals.haveTotals = true;
        setTotals(tmpTotals);            
    });

    const titleRow = <Row style={{ fontSize:'0.9em', fontWeight:'bold'}}>
    <Col xs='5'><Row>
        <Col style={{textAlign: 'left'}} xs='7'> Unit Name </Col>
        <Col style={{textAlign: 'left'}} xs='5'> Activity Name </Col>
        </Row>
    </Col>
    <Col xs='7'><Row>
        <Col style={{textAlign: 'center'}} xs='3'>Mastered / Attempts</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Highest</Col>
        <Col style={{textAlign: 'center'}} xs='1'>Last</Col>
        <Col style={{textAlign: 'center'}} xs='1'>Avg</Col>
        <Col style={{textAlign: 'center'}} xs='1'>Gain</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Time</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Date</Col>
        </Row></Col>
    </Row>;

    const headerComp = <center>
        <h4><Row>
            <Col xs='4'>{header.student}</Col> 
            <Col xs='4'>{header.program} </Col>
            <Col xs='4'>Activity Report</Col>
        </Row></h4>
    </center>;

    const Footer = ()=> <center><hr/>* Each teacher can set their own mastery level for the class and an activity is considered to be mastered when the student answers at least that {masterLevel*100}% of the questions correctly on any attempt</center>;

    return (
        <>
            {headerComp}
            <hr/>
            {titleRow}
            {tableData.map((moduleRec, module_i) => <span key={module_i}>
                {(moduleRec.length > 0) && <Row><Col xs='12' style={{fontWeight:'bold'}} >{moduleRec[0].moduleID[1]}</Col></Row>}
                {moduleRec.map((markRec, mark_i) => 
                    <TableRow key={mark_i} data={markRec} fontWeight={'normal'}/>
                )}
                </span>
            )}
            <TableRow data={totals} fontWeight={'bold'}/>
            <Footer/>
        </>
    );
};
ActivityReportComp.propTypes = {
    tableData: PropTypes.array
};

const TableRow = (props) => {
    let { unitName, name, mark} = props.data;
    const { TimesAttempted, TimesCompleted, HighestMark, LastMark, FirstMark, QuestionsRight, QuestionsAttempted, CumulativeTime, LastUsed} = mark;
    
    const checkUnitName=()=>{
        if(!unitName) return '';
        if(unitName.indexOf('~~') > -1)
            return '';
        return unitName;
    };

    const showDate=()=>{
        if(LastUsed)
            return timeConverter(parseInt(LastUsed));
        return '--';
    };

    const showImprovement=()=>{
        let improvement = 0;
        if(FirstMark && LastMark)
            improvement = LastMark - FirstMark;
        if(improvement !== 0)
            return improvement+'%';
        return '--';
    };

    return (
        <Row style={{fontSize:'0.9em'}}>
            <Col xs='5'><Row>
                <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='7'> {checkUnitName()} </Col>
                <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='5'>{name}</Col>
                </Row>
            </Col>
            <Col xs='7'><Row>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'>{TimesCompleted}/{TimesAttempted}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{HighestMark}%</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{LastMark}%</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{Math.round((QuestionsRight/QuestionsAttempted)*100)}%</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='1'>{showImprovement()}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{toTime(CumulativeTime)} {toTimeType(CumulativeTime)}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{showDate()}</Col>
            </Row></Col>
        </Row>
    );
};

TableRow.propTypes = {
    fontWeight: PropTypes.string,
    data: PropTypes.object
};

export default ContainerComp;